import { useEffect, useRef, useState, useCallback } from "react";

function wsBase() {
  const url = process.env.REACT_APP_BACKEND_URL || "";
  return url.replace(/^http/, "ws");
}

/**
 * Ouvre un WebSocket sur une opération. Présence + événements temps réel.
 * Auth : cookie httpOnly envoyé automatiquement lors du handshake (même origine).
 * onMessage(msg) reçoit les messages {type: journal|invalidate|notification|presence}.
 */
export function useOperationSocket(oid, onMessage) {
  const [presence, setPresence] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const pingRef = useRef(null);
  const deadRef = useRef(false);
  const cbRef = useRef(onMessage);
  cbRef.current = onMessage;

  const connect = useCallback(() => {
    if (!oid || deadRef.current) return;
    let ws;
    try {
      ws = new WebSocket(`${wsBase()}/api/ws/operations/${oid}`);
    } catch {
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      retryRef.current = 0;
      clearInterval(pingRef.current);
      pingRef.current = setInterval(() => {
        try { ws.send(JSON.stringify({ etat: "actif" })); } catch { /* noop */ }
      }, 25000);
    };
    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type === "presence") setPresence(msg.users || []);
      cbRef.current && cbRef.current(msg);
    };
    ws.onclose = () => {
      setConnected(false);
      clearInterval(pingRef.current);
      if (deadRef.current) return;
      const delay = Math.min(1000 * 2 ** retryRef.current, 15000);
      retryRef.current += 1;
      setTimeout(connect, delay);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
  }, [oid]);

  useEffect(() => {
    deadRef.current = false;
    connect();
    return () => {
      deadRef.current = true;
      clearInterval(pingRef.current);
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, [connect]);

  return { presence, connected };
}
